import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, X, Check } from "lucide-react";

export default function Cancel() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get(`/bookings/${id}/public?token=${token}`)
      .then((r) => setBooking(r.data))
      .catch(() => setBooking(null))
      .finally(() => setLoading(false));
  }, [id, token]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/bookings/${id}/cancel`, { token });
      setCancelled(true);
      toast.success("Appointment cancelled");
    } catch {
      toast.error("Could not cancel");
    } finally { setCancelling(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-espresso" /></div>;

  if (!booking) {
    return (
      <div className="pt-20 px-6 text-center" data-testid="cancel-invalid">
        <p className="script text-3xl text-gold">hmm</p>
        <h1 className="text-4xl font-serif text-espresso mt-1">Link not found</h1>
        <p className="text-mocha mt-3 text-sm">This cancellation link is invalid or already used.</p>
        <button onClick={() => navigate("/")} className="mt-6 px-6 py-3 rounded-full bg-espresso text-cream text-sm">Back home</button>
      </div>
    );
  }

  if (cancelled || booking.status === "cancelled") {
    return (
      <div className="pt-20 px-6 text-center" data-testid="cancel-done">
        <div className="w-20 h-20 rounded-full bg-blush/40 mx-auto flex items-center justify-center shadow-glow">
          <Check size={36} className="text-espresso" />
        </div>
        <p className="script text-3xl text-gold mt-6">all set</p>
        <h1 className="text-4xl font-serif text-espresso mt-1">Cancelled</h1>
        <p className="text-mocha mt-3 text-sm">Your appointment has been cancelled. We hope to see you again soon ♡</p>
        <button onClick={() => navigate("/")} className="mt-6 px-6 py-3 rounded-full bg-espresso text-cream text-sm">Back home</button>
      </div>
    );
  }

  return (
    <div className="pt-16 px-6 text-center" data-testid="cancel-page">
      <p className="script text-2xl text-gold">cancel appointment</p>
      <h1 className="text-4xl font-serif text-espresso mt-1">Are you sure?</h1>
      <div className="mt-8 bg-white rounded-[2rem] p-6 text-left shadow-soft">
        <p className="text-xs uppercase tracking-widest text-gold">Your appointment</p>
        <h3 className="text-2xl font-serif text-espresso mt-1">{booking.service_name}</h3>
        <div className="mt-4 space-y-2 text-sm text-espresso">
          <div className="flex justify-between"><span className="text-mocha">Date</span><span>{booking.date}</span></div>
          <div className="flex justify-between"><span className="text-mocha">Time</span><span>{booking.time}</span></div>
          <div className="flex justify-between"><span className="text-mocha">For</span><span>{booking.customer_name}</span></div>
        </div>
      </div>
      <button onClick={doCancel} disabled={cancelling} data-testid="confirm-cancel-btn"
        className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition text-sm">
        {cancelling ? <Loader2 size={16} className="animate-spin" /> : <X size={14} />} Yes, cancel it
      </button>
      <button onClick={() => navigate("/")} className="block mx-auto mt-4 text-mocha text-xs underline underline-offset-4">Keep my appointment</button>
    </div>
  );
}
